<template>
  <header class="header d-flex justify-content-between align-items-center" id="header">
    <div class="d-flex align-items-center menu-btn-toggle">
      <button class="menu-toggle me-2 menu-toggle-header" id="menuToggle">
        <span class="material-symbols-outlined">menu</span>
      </button>
    </div>

    <div class="logo-container">
      <h1>{{ currentPageTitle }}</h1>
    </div>

    <div class="d-flex header-profile align-items-center">
      <!-- Mail Icon - Hidden on small screens -->
      <span class="material-symbols-outlined me-3 d-none d-lg-inline">mail</span>

      <!-- Notification icon -->
      <div class="notification-icon position-relative mt-2" @click="toggleNotifications">
        <span class="material-symbols-outlined bell-icon me-3">notifications</span>
        <span v-if="unreadCount > 0" class="notification-badge me-3">
          {{ unreadCount }}
        </span>

        <!-- Dropdown popup -->
        <div v-if="showNotifications" class="notification-dropdown shadow p-2">
          <h6 class="mb-2">Notifications</h6>
          <ul class="list-unstyled mb-0">
            <template v-if="notifications.length">
              <li v-for="(note, index) in notifications" :key="index" class="notification-item py-1 px-2">
                <router-link :to="{ name: 'ViewPendingNotifications', query: { entityType: note.entityType, statusInd: note.statusInd } }"
                  class="d-flex justify-content-between align-items-center w-100">
                  <strong>{{ note.name }}</strong>
                  <span>({{ note.noOfElements }})</span>
                </router-link>
              </li>
            </template>
            <li v-else class="text-center text-muted py-2">
              No notifications
            </li>
          </ul>
        </div>
      </div>

      <!-- User Info - Responsive -->
      <div class="position-relative no-wrap d-none d-md-block me-2">
        <span class="main-header fw-bolder">Welcome, {{ userName }}</span>
        <p class="sub-header fw-semibold mb-0">{{ userRole }}</p>
        <p class="sub-header mb-0 d-none d-xl-inline">{{ currentTime }}</p>
      </div>

      <!-- Profile avatar and dropdown -->
      <img :src="userStore.avatar" alt="Profile" class="profile-avatar" />
      <div @click="toggleProfileDropdown" class="d-flex justify-content-center align-items-center">
        <span class="material-symbols-outlined">keyboard_arrow_down</span>

        <div v-if="showProfileDropdown" class="profile-dropdown shadow p-2">
          <div>
            <router-link :to="{ name: 'Profile' }" class="text-decoration-none">
              <button class="btn btn-sm btn-success d-flex justify-content-end align-items-center">
                View Profile
              </button>
            </router-link>
          </div>
        </div>
      </div>

      <!-- Logout button - Desktop only -->
      <button href="#" @click.prevent="logout" class="logout d-lg-flex align-items-center btn btn-sm btn-danger ms-3">
        <span class="material-symbols-outlined">logout</span>
      </button>

      <!-- Theme toggle -->
      <button @click="quickToggle" class="d-flex align-items-center btn btn-sm btn-outline-secondary ms-3">
        <span class="material-symbols-outlined">
          {{ isDark ? 'light_mode' : 'dark_mode' }}
        </span>
      </button>
    </div>
  </header>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import dayjs from 'dayjs'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useRoute } from 'vue-router'
import blankAvatar from '@/assets/images/blank-profile-avatar.png'

const router = useRouter()

const logout = () => {
  Cookies.remove('auth_token')
  router.push({ name: 'Login' })
}

import { useTheme } from '@/composables/useTheme'
const { theme, setTheme } = useTheme()
const quickToggle = () => {
  setTheme(theme.value === 'dark' ? 'light' : 'dark')
  isDark.value = !isDark.value
}

const isDark = ref(false)
watch(isDark, val => localStorage.setItem('theme', val ? 'dark' : 'light'))

// function toggleTheme() {
//   isDark.value = !isDark.value
//   emit('toggle-dark', isDark.value)
// }

const notifications = ref([])
const showNotifications = ref(false)
const showProfileDropdown = ref(false)
const userStore = useUserStore()
const userName = ref('User')
const userRole = ref('Role')
const userProfilePhoto = ref({ image: blankAvatar })
const route = useRoute()

const currentPageTitle = computed(() => {
  if (route.name === 'Profile') {
    return "My Profile"
  } else {
    return ''
  }
})

function getFormattedDate() {
  const now = new Date()
  const day = now.getDate()
  let suffix = 'th'
  if (day % 10 === 1 && day !== 11) suffix = 'st'
  else if (day % 10 === 2 && day !== 12) suffix = 'nd'
  else if (day % 10 === 3 && day !== 13) suffix = 'rd'
  return dayjs(now).format('MMMM D') + suffix + dayjs(now).format(', YYYY h:mmA')
}
const currentTime = ref(getFormattedDate())

function toggleNotifications() {
  showNotifications.value = !showNotifications.value
  showProfileDropdown.value = false
}

function toggleProfileDropdown() {
  showProfileDropdown.value = !showProfileDropdown.value
  showNotifications.value = false
}

function handleClickOutside(event) {
  const notifDropdown = document.querySelector('.notification-dropdown')
  const notifIcon = document.querySelector('.notification-icon')
  const profileDropdown = document.querySelector('.profile-dropdown')
  const profileContainer = document.querySelector('.header-profile')

  if (showNotifications.value && notifDropdown && !notifIcon.contains(event.target)) {
    showNotifications.value = false
  }

  if (showProfileDropdown.value && profileDropdown && profileContainer && !profileContainer.contains(event.target)) {
    showProfileDropdown.value = false
  }
}

async function fetchNotifications(userId) {
  try {
    const token = Cookies.get('auth_token')
    const rootApi = process.env.VUE_APP_API_ROOT_URL
    const { data } = await axios.get(`${rootApi}/notify/targetUser/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    })
    notifications.value = Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('Failed to fetch notifications:', e)
  }
}

async function fetchNotifications2(userId) {
  try {
    const token = Cookies.get('auth_token')
    const rootApi = process.env.VUE_APP_API_ROOT_URL
    await axios.get(`${rootApi}/notify/notifs/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    })
  } catch (e) {
    console.error('Failed to fetch notifications2:', e)
  }
}

const unreadCount = computed(() => notifications.value.length)

onMounted(() => {
  isDark.value = localStorage.getItem('theme') === 'dark'

  const timer = setInterval(() => {
    currentTime.value = getFormattedDate()
  }, 60000)

  if (userStore.profile?.id) fetchNotifications(userStore.profile.id)

  document.addEventListener('click', handleClickOutside)

  return () => {
    clearInterval(timer)
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})

watch(
  () => userStore.profile?.id,
  id => { if (id) fetchNotifications(id), fetchNotifications2(id) },
  { immediate: true }
)

watch(
  () => `${userStore.profile?.firstName || ''} ${userStore.profile?.lastName || ''}`.trim(),
  val => { userName.value = val || 'User' },
  { immediate: true }
)
watch(
  () => userStore.profile?.roleName,
  val => { userRole.value = val || 'Role' },
  { immediate: true }
)
watch(
  () => userStore?.profilePhoto,
  val => { userProfilePhoto.value = val || { image: blankAvatar } },
  { immediate: true },
)
</script>

<style scoped>
.btn-success {
    color: #fff !important;
    background-color: var(--btn-alt) !important;
    border-color: var(--btn-alt) !important;
}

.btn-success:hover {
    color: #fff !important;
    background-color: var(--btn-alt-hover) !important;
    border-color: var(--btn-alt-hover) !important;
}

.notification-icon {
  cursor: pointer;
  align-items: center;
  height: 100%;
}

.notification-badge {
  position: absolute;
  top: -10px;
  right: -8px;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  line-height: 1;
}

.notification-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 250px;
  background: white;
  border: 0.1px solid var(--border);
  border-radius: 4px;
  z-index: 1000;
  color: var(--color);
}

.notification-item {
  border-bottom: 1px solid #e9ecef;
}

.notification-item:last-child {
  border-bottom: none;
}

.profile-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 1.5px solid var(--btn-primary);
}

.profile-dropdown {
  position: absolute;
  top: 100%;
  width: auto;
  background: var(--bg);
  border-radius: 4px;
  z-index: 1000;
  color: #000;
  padding: 1rem;
}

.profile-item {
  border-bottom: 1px solid #e9ecef;
}

.material-symbols-outlined {
  font-size: 1.25rem;
}

.text-center.action .btn {
  background-color: var(--btn-alt) !important;
  border: none !important;
  border-radius: 0px !important;
  color: var(--color);
  font-weight: 600;
}

.logout {
  color: white;
  background-color: #ff4d4d;
  transition: background-color 0.3s ease;
}

.no-wrap {
  white-space: nowrap;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .profile-avatar {
    width: 35px;
    height: 35px;
  }

  .notification-dropdown,
  .profile-dropdown {
    right: -10px;
  }
}
</style>
